import React, { useId } from 'react';
import { cx } from '../cx';

// D-09 §15.27 — 테이블 (.dtable > thead/tbody)
// Figma DS `grid`(2805:1472) 정합 — 헤더 shell.bg / 격자선 shell.border / 선택행 groupTheme.subtle.
// 열 정의(columns) + 행 데이터(rows) 구동. 셀 정렬은 열이 소유한다(화면이 td 마다 클래스를 붙이지 않는다).
// 모바일은 카드(§15.3) 리스트로 전환한다 — 좁은 화면에서 이 컴포넌트를 가로 스크롤로 밀지 않는다.
//
// 펼침 행 지원(승인 N-02, D-07 §3.2 #20 예외) — renderExpanded 를 주면 행 아래에 전폭 영역(colSpan)이 열린다.
//  · 펼침 상태는 호출부가 소유한다(expandedKeys). 여러 행 동시 펼침 가능, 토글은 onRowClick 에서 호출부가 처리한다.
//  · 펼침 모드에서는 행 안의 버튼·링크·입력 요소 클릭과 키 입력이 행 클릭으로 올라오지 않는다(다음 할 일 버튼 등).
//  · renderExpanded·stickyHeader 를 주지 않으면 기존 동작과 마크업이 그대로다(하위 호환).

const ALIGN_CLASS = { left: 'ta-l', center: 'ta-c', right: 'ta-r' } as const;

/** 펼침 모드에서 행 클릭으로 취급하지 않는 내부 상호작용 요소 */
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, label, [role="button"], [data-row-click="ignore"]';

export interface TableColumn<T> {
  /** 열 식별자. render 가 없으면 이 키로 row 값을 읽는다 */
  key: string;
  header: React.ReactNode;
  /** 기본 center(시안). 금액·수량은 'right', 긴 텍스트는 'left' */
  align?: keyof typeof ALIGN_CLASS;
  /** 열 폭 — '160px' · '20%' 등. 생략하면 내용이 정한다 */
  width?: string;
  /** 셀 렌더 — Badge 등 노드를 넣을 때 쓴다 */
  render?: (row: T, index: number) => React.ReactNode;
}

export interface TableProps<T> extends Omit<React.TableHTMLAttributes<HTMLTableElement>, 'children'> {
  columns: readonly TableColumn<T>[];
  /** readonly 를 받는다 — 화면이 `as const` 픽스처나 동결된 목록을 그대로 넘길 수 있어야 한다 */
  rows: readonly T[];
  /** 행 식별자 — 선택 상태 대조와 React key 에 함께 쓴다 */
  rowKey: (row: T, index: number) => string | number;
  /** 선택된 행(.is-selected). 단일 선택만 다룬다 — 다중 선택은 체크박스 열(§15.12 bare)로 화면이 소유한다 */
  selectedKey?: string | number;
  /** 행 클릭 — 주면 행이 키보드로도 눌린다(Enter·Space) */
  onRowClick?: (row: T, index: number) => void;
  /** 0건 표시 — 로딩과 혼용하지 않는다(D-07 §3.2 #8). 로딩은 화면이 Skeleton 으로 그린다 */
  empty?: React.ReactNode;
  /** 펼침 영역 렌더 — 주면 펼침 모드. 행 아래에 열 전체 폭의 영역이 열린다 */
  renderExpanded?: (row: T, index: number) => React.ReactNode;
  /** 펼쳐진 행의 rowKey 집합(여러 행 동시 펼침). 펼침 모드에서만 쓴다 */
  expandedKeys?: ReadonlySet<string | number>;
  /** 펼침 모드의 맨 끝 열(⌄ 표시)을 붙일지. 기본 true */
  expandIndicator?: boolean;
  /** 머리글을 스크롤 영역 위쪽에 붙인다. 가까운 스크롤 조상(예: PageScroll) 기준 */
  stickyHeader?: boolean;
  /** sticky 머리글이 붙는 위치 — CSS 길이('0'·'var(--num-48)' 등). 기본 0 */
  stickyTop?: string;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  selectedKey,
  onRowClick,
  empty,
  renderExpanded,
  expandedKeys,
  expandIndicator = true,
  stickyHeader = false,
  stickyTop,
  className = '',
  style,
  ...props
}: TableProps<T>) {
  const baseId = useId();
  const expandable = renderExpanded != null;
  const showIndicator = expandable && expandIndicator;
  const colCount = columns.length + (showIndicator ? 1 : 0);
  const tableStyle = stickyTop != null ? ({ ['--dtable-sticky-top' as string]: stickyTop, ...style } as React.CSSProperties) : style;

  return (
    <table className={cx('dtable', stickyHeader && 'dtable-sticky', className)} style={tableStyle} {...props}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              scope="col"
              className={c.align ? ALIGN_CLASS[c.align] : undefined}
              style={c.width ? { width: c.width } : undefined}
            >
              {c.header}
            </th>
          ))}
          {showIndicator && <th scope="col" aria-label="펼침" style={{ width: 'var(--num-32)' }} />}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && empty != null ? (
          <tr>
            <td className="dtable-empty" colSpan={colCount}>{empty}</td>
          </tr>
        ) : (
          rows.map((row, i) => {
            const key = rowKey(row, i);
            const expanded = expandable && expandedKeys?.has(key) === true;
            const expandId = `${baseId}-x-${key}`;
            const clickable = onRowClick != null;
            const tr = (
              <tr
                key={key}
                className={cx(key === selectedKey && 'is-selected', expanded && 'is-expanded')}
                aria-selected={selectedKey != null ? key === selectedKey : undefined}
                aria-expanded={expandable ? expanded : undefined}
                aria-controls={expanded ? expandId : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={
                  clickable
                    ? (e) => {
                        if (expandable && (e.target as HTMLElement).closest(INTERACTIVE_SELECTOR) !== null) return;
                        onRowClick(row, i);
                      }
                    : undefined
                }
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        if (expandable && e.target !== e.currentTarget) return;
                        e.preventDefault();
                        onRowClick(row, i);
                      }
                    : undefined
                }
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.align ? ALIGN_CLASS[c.align] : undefined}>
                    {c.render ? c.render(row, i) : (row as Record<string, React.ReactNode>)[c.key]}
                  </td>
                ))}
                {showIndicator && (
                  <td className="dtable-expand-cell">
                    <span aria-hidden="true">⌄</span>
                  </td>
                )}
              </tr>
            );
            if (!expandable) return tr;
            return (
              <React.Fragment key={key}>
                {tr}
                {expanded && (
                  <tr id={expandId} className="dtable-expand-row">
                    <td colSpan={colCount}>
                      <div className="dtable-expand-body">{renderExpanded(row, i)}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        )}
      </tbody>
    </table>
  );
}
